import type { ReactNode } from "react";
import { useId } from "react";
import {
  useFormContext,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { FormControl } from "@mui/material";
import type { SelectProps } from "@mui/material";
import MuiSelect from "@mui/material/Select";
import MuiMenuItem from "@mui/material/MenuItem";
import type { MenuProps, SxProps } from "@mui/material";
import { Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { getValidationRules } from "./utils/getValidationRules";
import { FormControlLabel } from "./FormControlLabel";
import { HelperMessage } from "./HelperMessage";

const ITEM_HEIGHT = 48;

export const SelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5,
      width: 200,
    },
  },
  MenuListProps: {
    style: {
      padding: 0,
    },
  },
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
} as Partial<MenuProps>;

export const SelectStyles: SxProps = {
  width: "100%",
  ".MuiOutlinedInput-notchedOutline": { borderStyle: "none" },
};

interface SelectOption {
  value: string;
  label: string;
  iconSrc?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface FormSelectComponentProps {
  label: React.HTMLAttributes<HTMLLabelElement>;
}
interface FormSelectProps extends Omit<SelectProps, 'onChange'> {
  className?: string;
  label?: React.ReactNode;
  required?: boolean;
  rules?: RegisterOptions;
  name: string;
  componentProps?: FormSelectComponentProps;
  options?: string[] | SelectOption[];
  showError?: boolean;
  onChange?: (value: unknown) => void;
  placeholder?: string;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  className,
  name,
  label = "",
  required = true,
  rules = {},
  options,
  placeholder,
  componentProps,
  onChange: handleChange = () => null,
  children,
  disabled,
  ...rest
}) => {
  const id = useId();
  const t = useTranslations();

  const validationRules = getValidationRules({
    required,
    rules,
    t,
  });

  const { control } = useFormContext();

  const {
    field: { onChange, onBlur, value },
    fieldState: { error },
    formState: { errors },
  } = useController({
    name,
    rules: validationRules,
    control,
  });

  const renderedOptions = options?.map((option: string | SelectOption) => {
    const optionValue = typeof option === "string" ? option : option.value;
    const itemLabel = typeof option === "string" ? option : option.label;
    const iconSrc = typeof option === "string" ? "" : option.iconSrc;
    const icon = typeof option === "string" ? "" : option.icon;
    const isDisabled = typeof option === "string" ? false : option.disabled;

    return (
      <MuiMenuItem
        className="flex gap-2"
        data-cy={`${name}-option`}
        data-value={optionValue}
        disabled={isDisabled}
        key={optionValue}
        value={optionValue}
      >
        {Boolean(iconSrc) && (
          <img alt="" height={24} src={iconSrc as string} width={24} />
        )}
        {icon}
        <Typography variant="text_lg_m">{itemLabel}</Typography>
      </MuiMenuItem>
    );
  });

  const placeholderRender = (): ReactNode => {
    let selectedLabel;
    if (value && options) {
      if (typeof options[0] === "string") {
        selectedLabel = (options as string[]).find(
          (option) => option === value
        );
      } else if (typeof options[0] === "object") {
        selectedLabel = (options as SelectOption[]).find(
          (option) => option.value === value
        )?.label;
      }
    }
    // Hack to enable placeholder in MuiSelect which doesn't support placeholder by default
    // because there is a label already
    // Details: https://github.com/mui/material-ui/issues/11069
    return selectedLabel ? (
      <Typography variant="labelL">{selectedLabel}</Typography>
    ) : (
      <Typography color="text.support" variant="labelL">
        {placeholder}
      </Typography>
    );
  };

  return (
    <FormControl className={className ?? "flex flex-1 flex-col"}>
      <FormControlLabel
        id={id}
        label={label}
        labelProps={componentProps?.label}
      />
      <MuiSelect
        MenuProps={SelectMenuProps}
        data-cy={name}
        disabled={disabled}
        displayEmpty
        error={Boolean(error)}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={(event) => {
          onChange(event);
          handleChange(event.target.value);
        }}
        renderValue={placeholder ? placeholderRender : undefined}
        size="small"
        sx={SelectStyles}
        value={value === undefined ? "" : (value as string)}
        variant="outlined"
        {...rest}
      >
        {renderedOptions ?? children ?? null}
      </MuiSelect>
      <HelperMessage errors={errors} name={name} />
    </FormControl>
  );
};
